import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, Clock, AlertCircle, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Bell, Mail, MessageSquare, BarChart3, Layers, Square, StopCircle, CreditCard } from 'lucide-react';
import { httpClient } from '../services/httpClient';
import {
  shuffleTips, categoryConfig,
  estimateDurationRange, formatDurationRange, DURATION_DISCLAIMER,
  PIPELINE_NODES, PIPELINE_EDGES, computeNodeStates,
} from './progress/analysisContent';
import { PipelineGraph, SEGMENTS, deriveSegmentState } from './progress/PipelineGraph';

interface DocumentInfo {
  documentId: string;
  filename: string;
  pageCount: number;
  blankPageCount: number;
  fileSize: number;
}

interface ProgressEvent {
  message: string;
  stage: string;
  documentName: string | null;
  timestamp: string;
}

interface PipelineDiagnostics {
  mapChunksTotal: number;
  mapChunksCompleted: number;
  mapChunksFailed: number;
  mapChunksRetried: number;
  mapChunksTruncated: number;
  truncationCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  eventsExtracted: number;
  eventsFinal: number;
  totalDurationMs: number;
}

interface ProgressData {
  stage: string;
  progress: number;
  complete: boolean;
  failed: boolean;
  cancelled?: boolean;
  error: string | null;
  events: ProgressEvent[];
  startedAtMillis: number;
  warnings?: string[];
  diagnostics?: PipelineDiagnostics;
}

interface AnalysisProgressPanelProps {
  documents: DocumentInfo[];
  caseId: string;
  onRetry?: () => void;
  onComplete?: (success: boolean) => void;
}

function friendlyError(raw?: string | null): string {
  if (!raw) return 'An unexpected error occurred during analysis.';
  if (raw.includes('Insufficient page') || raw.includes('insufficient') || raw.includes('used all your pages'))
    return 'You don\u2019t have enough page credits to process this document. Please top up your credits or subscribe to continue.';
  if (raw.includes('target server failed to respond') || raw.includes('Unable to execute HTTP request'))
    return 'The AI service is temporarily unavailable. This is usually a transient issue — please retry.';
  if (raw.includes('throttling') || raw.includes('ThrottlingException') || raw.includes('Too many requests'))
    return 'The AI service is currently busy. Please wait a moment and retry.';
  if (raw.includes('timeout') || raw.includes('timed out'))
    return 'The request timed out. This may happen with large documents — please retry.';
  if (raw.includes('AccessDeniedException') || raw.includes('not authorized') || raw.includes('marketplace'))
    return 'The AI service is not properly configured. Please contact support.';
  if (raw.includes('ValidationException'))
    return 'An internal configuration error occurred. Please contact support.';
  if (raw.includes('ServiceQuota'))
    return 'The AI service has reached its usage limit. Please try again later.';
  return 'Analysis failed. Please retry or contact support if the problem persists.';
}

type DocStatus = 'waiting' | 'analyzing' | 'complete';

function getDocStatusFromEvents(filename: string, events: ProgressEvent[], isComplete: boolean): { status: DocStatus; latestMessage: string } {
  const docEvents = events.filter(e => e.documentName === filename);
  if (docEvents.length === 0) {
    return { status: isComplete ? 'complete' : 'waiting', latestMessage: '' };
  }
  const lastEvent = docEvents[docEvents.length - 1];
  const completed = lastEvent.message.startsWith('Finished reading ');
  return {
    status: isComplete || completed ? 'complete' : 'analyzing',
    latestMessage: lastEvent.message,
  };
}

// Compute per-phase start/end times from the event stream.
// Respects graph-edge dependencies: a node's timer only starts once all
// predecessor nodes (per PIPELINE_EDGES) have completed.
function computePhaseTimings(
  events: ProgressEvent[],
): Map<string, { startMs: number; endMs?: number }> {
  const timings = new Map<string, { startMs: number; endMs?: number }>();

  const predecessors = new Map<string, string[]>();
  for (const edge of PIPELINE_EDGES) {
    const preds = predecessors.get(edge.to) ?? [];
    preds.push(edge.from);
    predecessors.set(edge.to, preds);
  }
  const completedNodes = new Set<string>();

  for (const event of events) {
    if (!event.stage || !event.timestamp) continue;
    const ts = new Date(event.timestamp).getTime();

    // First: mark completions so downstream nodes can activate
    for (const node of PIPELINE_NODES) {
      if (node.completedWhen.includes(event.stage)) {
        completedNodes.add(node.id);
        const timing = timings.get(node.id);
        if (timing && !timing.endMs) {
          timing.endMs = ts;
        }
      }
    }

    // Second: start timers only when all predecessors are complete
    for (const node of PIPELINE_NODES) {
      if (node.activeWhen.includes(event.stage) && !timings.has(node.id)) {
        const preds = predecessors.get(node.id) ?? [];
        if (preds.every(pid => completedNodes.has(pid))) {
          timings.set(node.id, { startMs: ts });
        }
      }
    }
  }

  return timings;
}

interface NotifPrefs {
  email: boolean;
  sms: boolean;
  hasPhone: boolean;
}

export const AnalysisProgressPanel: React.FC<AnalysisProgressPanelProps> = ({ documents, caseId, onRetry, onComplete }) => {
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stoppedRef = useRef(false);
  const maxProgressRef = useRef(0);
  const lastFailedStartRef = useRef<number | null>(null);
  const completeFiredRef = useRef(false);

  // Stop analysis state
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [stopping, setStopping] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState<NotifPrefs | null>(null);

  const totalPages = documents.reduce((sum, d) => sum + (d.pageCount || 0), 0);
  const pagesKnown = totalPages > 0 || documents.length === 0;
  const docsWithPages = documents.filter(d => d.pageCount > 0).length;
  const docsWithoutPages = documents.length - docsWithPages;
  const durationEstimate = useMemo(() => pagesKnown ? estimateDurationRange(totalPages, documents.length) : null, [totalPages, documents.length, pagesKnown]);

  // Tips carousel state
  const tips = useMemo(() => shuffleTips(caseId), [caseId]);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [tipPaused, setTipPaused] = useState(false);
  const [warningsExpanded, setWarningsExpanded] = useState(false);

  const nextTip = useCallback(() => setCurrentTipIndex(i => (i + 1) % tips.length), [tips.length]);
  const prevTip = useCallback(() => setCurrentTipIndex(i => (i - 1 + tips.length) % tips.length), [tips.length]);

  // Auto-advance tips every 20 seconds (pauses on hover)
  useEffect(() => {
    if (tipPaused || progressData?.complete || progressData?.failed) return;
    const interval = setInterval(nextTip, 20000);
    return () => clearInterval(interval);
  }, [tipPaused, nextTip, progressData?.complete, progressData?.failed]);

  // Fetch notification preferences on mount
  useEffect(() => {
    httpClient.get<{ analysisEmail: boolean; analysisSms: boolean; smsPhoneNumber?: string | null }>('/notifications/preferences')
      .then(data => {
        setNotifPrefs({
          email: data.analysisEmail,
          sms: data.analysisSms,
          hasPhone: !!data.smsPhoneNumber,
        });
      })
      .catch(() => { /* silently fail — non-critical */ });
  }, []);

  const toggleNotifPref = useCallback((key: 'email' | 'sms', value: boolean) => {
    setNotifPrefs(prev => prev ? { ...prev, [key]: value } : prev);
    const body = key === 'email' ? { analysisEmail: value } : { analysisSms: value };
    httpClient.put('/notifications/preferences', body).catch(() => {
      // Revert on failure
      setNotifPrefs(prev => prev ? { ...prev, [key]: !value } : prev);
    });
  }, []);

  const handleRetry = () => {
    lastFailedStartRef.current = progressData?.startedAtMillis ?? null;
    stoppedRef.current = false;
    maxProgressRef.current = 0;
    setProgressData(null);
    setElapsed(0);
    setRetryCount(c => c + 1);
    onRetry?.();
  };

  const handleStopAnalysis = async () => {
    setStopping(true);
    try {
      await httpClient.post(`/intelligence/metadata/cancel/${caseId}`, {});
    } catch {
      // Cancellation may still succeed server-side — polling will pick up the state
    } finally {
      setStopping(false);
      setShowStopConfirm(false);
    }
  };

  // Elapsed time derived from server's startedAtMillis — survives remounts.
  // Stops ticking once analysis reaches a terminal state (complete/failed/cancelled).
  useEffect(() => {
    if (!progressData?.startedAtMillis) return;

    // One final tick to capture the elapsed time at the moment the terminal state is detected
    setElapsed(Math.round((Date.now() - progressData.startedAtMillis) / 1000));

    // Don't keep ticking if already terminal
    if (progressData.complete || progressData.failed) return;

    timerRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - progressData.startedAtMillis) / 1000));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [progressData?.startedAtMillis, progressData?.complete, progressData?.failed]);

  // Poll progress endpoint every 3 seconds
  useEffect(() => {
    const fetchProgress = async () => {
      if (stoppedRef.current) return;
      try {
        const data: ProgressData = await httpClient.get(`/intelligence/metadata/progress/${caseId}`);
        if (data.failed && lastFailedStartRef.current != null && data.startedAtMillis === lastFailedStartRef.current) {
          return;
        }
        lastFailedStartRef.current = null;
        setProgressData(data);
        if (data.complete || data.failed) {
          stoppedRef.current = true;
          if (pollingRef.current) clearInterval(pollingRef.current);
          // Don't fire onComplete when cancelled — keep the panel mounted
          // so the user sees the "Analysis Stopped" state with restart option
          if (!completeFiredRef.current && !data.cancelled) {
            completeFiredRef.current = true;
            onComplete?.(!data.failed);
          }
        }
      } catch {
        // Network/auth error — keep polling
      }
    };

    fetchProgress();
    pollingRef.current = setInterval(fetchProgress, 3000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [caseId, retryCount]);


  // Deduplicate consecutive events with the same message
  const rawEvents = progressData?.events ?? [];
  const events = rawEvents.reduce<(ProgressEvent & { count: number })[]>((acc, event) => {
    const prev = acc[acc.length - 1];
    if (prev && prev.message === event.message) {
      prev.count += 1;
      prev.timestamp = event.timestamp;
    } else {
      acc.push({ ...event, count: 1 });
    }
    return acc;
  }, []);
  const rawProgress = progressData ? Math.round(progressData.progress * 100) : 0;
  if (rawProgress > maxProgressRef.current) {
    maxProgressRef.current = rawProgress;
  }
  const overallProgress = maxProgressRef.current;
  const isFailed = progressData?.failed ?? false;
  const isComplete = progressData?.complete ?? false;
  const isCancelled = progressData?.cancelled ?? false;
  const isRunning = !isFailed && !isComplete;

  const phaseTimings = computePhaseTimings(rawEvents);
  const nodeStates = useMemo(
    () => computeNodeStates(rawEvents, isComplete, isFailed),
    [rawEvents, isComplete, isFailed],
  );

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatElapsedFriendly = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    if (m < 1) return 'less than 1 min';
    return `${m} min`;
  };

  // Time overrun check
  const isOverrunning = durationEstimate ? elapsed > durationEstimate.highMinutes * 60 : false;

  return (
    <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden max-h-[80vh] flex flex-col">
      {/* Header + Overall Progress */}
      <div className="pl-3 pr-5 pt-3 pb-1 border-b border-gray-100 dark:border-medical-700 flex-shrink-0">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-medical-100 flex items-center gap-2">
            {isCancelled ? (
              <StopCircle className="w-5 h-5 text-amber-500" />
            ) : isFailed ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500" />
              </span>
            )}
            {isCancelled ? 'Analysis Stopped' : isFailed ? 'Analysis Failed' : 'Analysing Evidence'}
            {!isFailed && !isCancelled && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 text-sm font-semibold tabular-nums ml-1">
                <Clock className="w-4 h-4" />
                {formatTime(elapsed)}
              </span>
            )}
          </h3>
          {!isFailed ? (
            <div className="flex items-center gap-4 pt-1">
              <div className="text-right leading-tight">
                {isRunning && (
                  <div className={`text-sm ${isOverrunning ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-medical-400'}`}>
                    {isOverrunning
                      ? `longer than est. ${durationEstimate ? formatDurationRange(durationEstimate.lowMinutes, durationEstimate.highMinutes) : ''}`
                      : durationEstimate
                        ? `est. ${formatDurationRange(durationEstimate.lowMinutes, durationEstimate.highMinutes)}`
                        : 'estimating\u2026'}
                  </div>
                )}
                <div className="flex items-center justify-end gap-3 text-sm text-gray-500 dark:text-medical-400 mt-1.5">
                  <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{documents.length}</span>
                  <span className="inline-flex items-center gap-1">
                    <Layers className="w-3.5 h-3.5" />
                    {pagesKnown
                      ? totalPages
                      : docsWithPages > 0
                        ? `${totalPages}\u2026`
                        : '\u2026'}
                  </span>
                </div>
              </div>
              <div className="relative w-14 h-14 flex-shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36" shapeRendering="geometricPrecision">
                  <defs>
                    <mask id="progressSegments">
                      <circle cx="18" cy="18" r="15.9155" fill="none" stroke="white" strokeWidth="5" strokeDasharray="3 1" />
                    </mask>
                  </defs>
                  <g mask="url(#progressSegments)">
                    <circle cx="18" cy="18" r="15.9155" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-gray-200 dark:text-medical-700" />
                    <circle cx="18" cy="18" r="15.9155" fill="none" stroke="currentColor" strokeWidth="3.5"
                      strokeDasharray={`${overallProgress} 100`}
                      className={`${isComplete ? 'text-green-500' : 'text-cyan-500'} transition-all duration-700 ease-out`}
                    />
                  </g>
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-700 dark:text-medical-200">
                  {overallProgress}%
                </span>
              </div>
            </div>
          ) : isCancelled ? (
            <div className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-medical-500">
              <Clock className="w-4 h-4" />
              <span className="tabular-nums">{formatTime(elapsed)}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-medical-400">
              <Clock className="w-4 h-4" />
              <span>{formatTime(elapsed)}</span>
            </div>
          )}
        </div>



        {isCancelled ? (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 space-y-3">
            <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
              Analysis was stopped at {overallProgress}% completion.
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Unused page credits have been refunded proportionally to your account.
            </p>
            {onRetry && (
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-md transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Restart Analysis
              </button>
            )}
          </div>
        ) : isFailed ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 space-y-3">
            <p className="text-sm text-red-700 dark:text-red-300 font-medium">
              {friendlyError(progressData?.error)}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {progressData?.error && (progressData.error.includes('Insufficient') || progressData.error.includes('insufficient') || progressData.error.includes('used all your pages')) && (
                <Link
                  to="/account"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  View plans &amp; billing
                </Link>
              )}
              {onRetry && (
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-md transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Pipeline progress bar */}
            <div className="max-w-3xl mx-auto -mt-3 mb-0">
              <PipelineGraph
                nodeStates={nodeStates}
                phaseTimings={phaseTimings}
              />
            </div>
            <div className="flex items-center justify-between mt-4 mb-4">
              <div className="flex-1 flex justify-center">
                {(() => {
                  const segStates = SEGMENTS.map(seg => deriveSegmentState(seg.nodeIds, nodeStates));
                  const activeIdx = segStates.lastIndexOf('active');
                  const statusSeg = activeIdx >= 0
                    ? SEGMENTS[activeIdx]
                    : segStates.every(s => s === 'completed') ? null : SEGMENTS[0];
                  if (!statusSeg) return null;
                  // Map segment label to pill colour classes
                  const pillColours: Record<string, string> = {
                    'Ingest': 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
                    'Review': 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
                    'Cross-reference': 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
                    'Verify': 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                    'Analyse': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
                  };
                  const colourClass = pillColours[statusSeg.label] ?? 'bg-gray-100 text-gray-600 dark:bg-medical-700 dark:text-medical-300';
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium animate-fade-in ${colourClass}`}>
                      {statusSeg.description}
                    </span>
                  );
                })()}
              </div>
              <button
                onClick={() => setShowStopConfirm(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors flex-shrink-0"
                title="Stop analysis"
              >
                <Square className="w-3 h-3 fill-current" />
                Stop
              </button>
            </div>
          </>
        )}

      </div>

      {/* Scrollable content area — everything below header scrolls together */}
      <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>

        {/* Credit nudge — shown when analysis completes but credits were insufficient */}
        {isComplete && progressData.warnings?.some(w => w.startsWith('CREDIT_INSUFFICIENT:')) && (
          <div className="px-5 pt-3">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Your page credit balance is too low to cover all documents in this case.
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  Analysis has completed, but future cases may be interrupted.
                  Please top up your credits or subscribe to a plan.
                </p>
                <Link
                  to="/account"
                  className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  View plans &amp; billing
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Warnings banner — shown when analysis completes with non-credit warnings */}
        {isComplete && progressData.warnings && progressData.warnings.filter(w => !w.startsWith('CREDIT_INSUFFICIENT:')).length > 0 && (
          <div className="px-5 pt-3">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
              <button
                type="button"
                onClick={() => setWarningsExpanded(v => !v)}
                className="flex items-center gap-2 cursor-pointer w-full text-left"
              >
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Analysis completed with warnings
                </span>
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  ({progressData.warnings.filter(w => !w.startsWith('CREDIT_INSUFFICIENT:')).length})
                </span>
                <ChevronRight
                  className={`w-3.5 h-3.5 text-amber-600 dark:text-amber-400 transition-transform duration-200 ${warningsExpanded ? 'rotate-90' : ''}`}
                />
              </button>
              {warningsExpanded && (
                <ul className="space-y-1 ml-6 mt-1.5">
                  {progressData.warnings.filter(w => !w.startsWith('CREDIT_INSUFFICIENT:')).map((warning, i) => (
                    <li key={i} className="text-sm text-amber-700 dark:text-amber-400 list-disc">
                      {warning}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Pipeline Stats — shown when analysis completes with diagnostics */}
        {isComplete && progressData?.diagnostics && (() => {
          const d = progressData.diagnostics;
          const formatTokens = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : `${n}`;
          const formatDuration = (ms: number) => {
            const totalSec = Math.round(ms / 1000);
            const m = Math.floor(totalSec / 60);
            const s = totalSec % 60;
            return m > 0 ? `${m}m ${s}s` : `${s}s`;
          };
          const truncatedLabel = d.mapChunksTruncated > 0 ? ` (${d.mapChunksTruncated} truncated)` : '';
          const failedLabel = d.mapChunksFailed > 0 ? ` · ${d.mapChunksFailed} failed` : '';
          return (
            <div className="px-5 pt-3">
              <div className="bg-gray-50 dark:bg-medical-700/30 border border-gray-200 dark:border-medical-600 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="w-4 h-4 text-gray-500 dark:text-medical-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-medical-300">Pipeline stats</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-medical-400">
                  {d.mapChunksCompleted}/{d.mapChunksTotal} chunks{truncatedLabel}{failedLabel}
                  {' · '}
                  {d.eventsExtracted} → {d.eventsFinal} events
                  {' · '}
                  {formatTokens(d.totalInputTokens)} in / {formatTokens(d.totalOutputTokens)} out
                  {' · '}
                  {formatDuration(d.totalDurationMs)}
                </p>
              </div>
            </div>
          );
        })()}

        {/* Navigate-away reassurance + notification preferences */}
        {isRunning && (
          <div className="bg-gray-100 dark:bg-medical-800 border-y border-gray-200 dark:border-medical-700 px-5 py-2.5">
            <div className="flex items-center gap-3 flex-wrap text-sm text-gray-800 dark:text-medical-200">
              <span>You can navigate away &mdash; analysis continues in the background. We can notify you when completed via</span>
              {notifPrefs && (
                <>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-gray-900 dark:text-medical-100">
                    <input
                      type="checkbox"
                      checked={notifPrefs.email}
                      onChange={(e) => toggleNotifPref('email', e.target.checked)}
                      className="rounded border-gray-400 dark:border-medical-600 text-cyan-600 focus:ring-cyan-500 h-4 w-4"
                    />
                    <Mail className="w-3.5 h-3.5" />
                    Email
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-gray-900 dark:text-medical-100">
                    <input
                      type="checkbox"
                      checked={notifPrefs.sms}
                      onChange={(e) => toggleNotifPref('sms', e.target.checked)}
                      className="rounded border-gray-400 dark:border-medical-600 text-cyan-600 focus:ring-cyan-500 h-4 w-4"
                    />
                    <MessageSquare className="w-3.5 h-3.5" />
                    Text message
                  </label>
                </>
              )}
            </div>
            {notifPrefs?.sms && !notifPrefs.hasPhone && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                Add your mobile number in <Link to="/settings" className="underline hover:text-amber-700 dark:hover:text-amber-300">Settings</Link> to receive texts
              </p>
            )}
          </div>
        )}



        {/* Tips Carousel */}
        {isRunning && tips.length > 0 && (
          <div
            className=""
            onMouseEnter={() => setTipPaused(true)}
            onMouseLeave={() => setTipPaused(false)}
          >
            {(() => {
              const tip = tips[currentTipIndex];
              const config = categoryConfig[tip.category];
              const TipIcon = tip.icon;
              return (
                <div className={`border-l-2 ${config.borderColor} bg-gray-50 dark:bg-medical-700/50 rounded-r-lg px-4 py-3`}>
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${config.badgeBg} flex items-center justify-center`}>
                        <TipIcon className={`w-4 h-4 ${config.badgeText}`} />
                      </div>
                      <div className="min-w-0">
                        <span className={`inline-block text-xs font-medium ${config.badgeText} ${config.badgeBg} rounded px-1.5 py-0.5 mb-0.5`}>
                          {config.label}
                        </span>
                        <span key={`title-${currentTipIndex}`} className="block text-sm font-semibold text-gray-800 dark:text-medical-100 animate-fade-in truncate">
                          {tip.title}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={prevTip}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-medical-600 text-gray-400 dark:text-medical-400 transition-colors"
                        aria-label="Previous tip"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={nextTip}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-medical-600 text-gray-400 dark:text-medical-400 transition-colors"
                        aria-label="Next tip"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div key={`body-${currentTipIndex}`} className="text-sm text-gray-600 dark:text-medical-300 leading-relaxed animate-fade-in ml-[42px]">
                    {tip.text}
                  </div>
                  {tip.reference && (
                    <p key={`ref-${currentTipIndex}`} className="mt-2 text-xs text-gray-400 dark:text-medical-500 italic animate-fade-in ml-[42px]">
                      Ref: {tip.reference}
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}


      </div>

      {/* Stop Analysis Confirmation Modal */}
      {showStopConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !stopping && setShowStopConfirm(false)} />
          <div className="relative bg-white dark:bg-medical-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-medical-700 max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-medical-100">Stop Analysis?</h4>
                <p className="text-sm text-gray-600 dark:text-medical-300 mt-1">
                  Are you sure you want to stop the analysis? Any partial results will be discarded.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowStopConfirm(false)}
                disabled={stopping}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-medical-300 bg-gray-100 dark:bg-medical-700 hover:bg-gray-200 dark:hover:bg-medical-600 rounded-lg transition-colors disabled:opacity-50"
              >
                Continue Analysis
              </button>
              <button
                onClick={handleStopAnalysis}
                disabled={stopping}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {stopping ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                    Stopping…
                  </>
                ) : (
                  <>
                    <Square className="w-3.5 h-3.5 fill-current" />
                    Stop Analysis
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
